import React, { useState, useEffect, useRef } from 'react'
import { callPostApi } from '../../api/ApiCaller'
import { SET_GAME_BONUS_RESULT_POST } from '../../api/ApiConst'
import { getAudioVolume } from '../../utils/CookiesUtils'
import { EXIT_FANFARE_MS, playExitFanfare, warmExitFanfare } from './exitFanfare'
import './RefrigeratorBonus.css'

// door gif = 15 frames x 200ms = 3s (plays once as the opening intro)
const DOOR_DURATION = 2500

// background music while the bonus is open — set to false to mute it
const BG_MUSIC = true

// how long the round holds after its last card before the fridge closes. Must stay
// >= the 4s blink in .fridge-bonus-num.is-max, or the fridge shuts while the winning
// numbers are still flashing.
const WIN_HOLD = 4000

// once the set is complete the shelves are emptied out: every card still shut pops
// open on its own, one every AUTO_OPEN_STEP ms, in a random order.
const AUTO_OPEN_STEP = 350

// what a leftover card shows when the pool below is empty — only reachable if the
// round somehow ends without a single priced card on the shelves.
const DEFAULT_BOX_VALUE = 100

const BASE = './ice-assets/refrigerator-bonus'

// One entry per penguin CHARACTER. Everything here travels with the character
// wherever it gets dealt — it says nothing about which shelf it lands on:
//   img    – static picture (only used if the character has no `anim`)
//   anim   – sprite sheet
//   frames – how many frames to actually play
//   cols/rows/row – the sheet's grid, when it is NOT a single row of `frames`.
//     005/006/007 are the raw two-row exports: row 0 opens with a red glow frame
//     and row 1 repeats the same reveal clean, so we play row 1. `cols` must be the
//     sheet's REAL column count (005 has 10 columns but only 9 frames on row 1) or
//     every frame lands off-centre. Defaults: cols = frames, rows = 1, row = 0.
//   aw/ah  – one cell's pixel size (drives the element's aspect ratio)
//   numLeft/numTop – %-position where the bonus number sits on THIS one's card
//   sfx    – its own reveal sound (otherwise the generic card-pull)
//   cls    – extra class on that penguin's element, for one-off styling
// (008/009 are byte-identical to 004, so they are not separate characters.)
const CHARACTERS = [
    { img: 'spritePenguin001.png', anim: 'spritePenguin001Animation_clean.png', frames: 7, aw: 143, ah: 162, numTop: 66 },
    { img: 'spritePenguin002.png', anim: 'spritePenguin002Animation_clean.png', frames: 5, aw: 178, ah: 213, numTop: 52 },
    { img: 'spritePenguin003.png', anim: 'spritePenguin003Animation_clean.png', frames: 8, aw: 169, ah: 156, numLeft: 48, numTop: 58, sfx: 'ani_pinguim_caixa.wav' },
    { img: 'spritePenguin004.png', anim: 'spritePenguin004Animation_clean.png', frames: 7, aw: 168, ah: 197, numTop: 56 },
    { img: 'spritePenguin005.png', anim: 'spritePenguin005Animation_clean.png', frames: 9, cols: 10, rows: 2, row: 1, aw: 180, ah: 201, numLeft: 48, numTop: 52 },
    { img: 'spritePenguin006.png', anim: 'spritePenguin006Animation_clean.png', frames: 8, cols: 8, rows: 2, row: 1, aw: 180, ah: 201, numLeft: 47, numTop: 62 },
    { img: 'spritePenguin007.png', anim: 'spritePenguin007Animation_clean.png', frames: 6, cols: 7, rows: 2, row: 1, aw: 180, ah: 201, numLeft: 44, numTop: 56 },
    { img: 'spritePenguin0010.png', anim: 'spritePenguin0010Animation_clean.png', frames: 7, aw: 162, ah: 160, numTop: 62, sfx: 'ani_pinguim_bunda.wav' },
    { img: 'spritePenguin0011.png', anim: 'spritePenguin0011Animation_clean.png', frames: 4, aw: 328, ah: 227, numLeft: 68, numTop: 32, sfx: 'ani_pinguim_chapeu.wav', cls: 'ac-penguin-fridge' },
]

// The shelf spots, in reading order. left/top are % of the fridge stage (so they
// scale WITH the fridge and stay locked to the shelves at any screen size). A slot
// may carry `cls` too — it is added on top of the character's own.
const SLOTS = [
    // left panel (open door) — 3 shelves x 2
    { left: 16, top: 6 },
    { left: 34, top: 7 },
    { left: 18, top: 38 },
    { left: 35, top: 38 },
    { left: 22, top: 70 },
    { left: 35, top: 69 },
    // right panel (fridge body) — top 2, middle 2, bottom 1
    { left: 52, top: 6 },
    { left: 67, top: 8 },
    { left: 51, top: 30 },
    { left: 67, top: 31 },
    { left: 57, top: 57 },   // the AC tub — PINNED_SLOT, see below
]

// The hat penguin sits in the AC tub at the bottom of the fridge and nowhere else:
// it is the widest sprite (328x227, hence its own .ac-penguin-fridge height) and the
// only one drawn to tuck in behind ac-fridge.png. So it is pinned to that slot and
// kept out of the shuffle — every other penguin still lands somewhere different
// each time the bonus opens.
const PINNED_CHAR = 'spritePenguin0011.png'
const PINNED_SLOT = 10

// Fisher-Yates on a copy — CHARACTERS itself must stay put, it is module state.
function shuffled(list) {
    const a = [...list]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
            ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
}

// slot last: `left`/`top` are the shelf's, never the character's
const place = (char, slot) => ({ ...char, ...slot, cls: `${char.cls || ''} ${slot.cls || ''}`.trim() })

// Deal the characters onto the shelves — a fresh layout every time the bonus opens.
// The pinned one is set aside first; the rest are dealt across the slots that are
// left. There are more of those slots (10) than characters to fill them (8), so the
// deck is dealt out whole and only then topped up from a re-shuffle: every character
// is guaranteed to show up, and the leftover slots are the only ones that repeat one.
function dealPenguins() {
    const pinned = CHARACTERS.find((c) => c.img === PINNED_CHAR)
    const rest = CHARACTERS.filter((c) => c !== pinned)
    const openSlots = SLOTS.map((slot, i) => i).filter((i) => i !== PINNED_SLOT)

    const deck = shuffled(rest)
    while (deck.length < openSlots.length) deck.push(...shuffled(rest).slice(0, openSlots.length - deck.length))

    const out = []
    openSlots.forEach((slotIdx, n) => { out[slotIdx] = place(deck[n], SLOTS[slotIdx]) })
    out[PINNED_SLOT] = place(pinned, SLOTS[PINNED_SLOT])
    return out
}

// shelf front-rail overlays (mask2remake) placed in front of the penguins.
// left/top/width are % of the fridge stage — tweak to line up with each shelf.
const SHELVES = [
    { left: 50, top: 74, width: 24 },
]

function RefrigeratorBonus({ gameSessionRecordId, bonusType, onBonusRoundCompleted }) {
    const [done, setDone] = useState(false)
    // which penguins have been clicked (their reveal animation is playing/played)
    const [revealed, setRevealed] = useState({})
    // bonus number shown on each revealed card — getBonusResult's `boxValue`
    const [prizes, setPrizes] = useState({})
    // cards the auto-reveal filled in after the round was won. Their numbers are
    // decoration, so everything that decides the round has to skip them.
    const [filler, setFiller] = useState({})
    // The backend's verdict, and the ONLY thing that decides this round. It flips
    // `matched` on the card that completes a set of three and pays `bonus` for it;
    // until then every card answers bonus = 0 and matched = false.
    const [won, setWon] = useState(null)
    // the number printed on that matched card — the only value that ever goes red
    const [wonVal, setWonVal] = useState(null)
    // a pick is in flight — one card at a time, so two clicks cannot race
    const pickPendingRef = useRef(false)
    // what the round has paid so far; handed back to the game when the fridge closes
    const totalWinRef = useRef(0)
    // the looping track, so the exit fanfare can take the scene to itself
    const bgRef = useRef(null)
    // the hand-back is scheduled — the close-out effect re-runs on every card the
    // auto-reveal opens, and this is what keeps it from being rescheduled
    const closingRef = useRef(false)
    const exitTimerRef = useRef(null)
    // cache-buster so the gif always restarts from its first frame on mount
    const [gifSrc] = useState(() => `${BASE}/door_animation/gif-maker.gif?t=${Date.now()}`)
    // this round's layout — dealt once on mount, so it is fixed while the bonus is
    // open and reshuffles the next time it opens
    const [penguins] = useState(dealPenguins)

    // once the door has finished opening, reveal the final fridge + penguins.
    // the fridge-opening sound plays along with the door animation.
    useEffect(() => {
        const openSfx = new Audio('assets/ice-bonus/abre_geladeira.wav')
        openSfx.volume = getAudioVolume()
        openSfx.play().catch(() => { })

        const t = setTimeout(() => setDone(true), DOOR_DURATION)
        return () => {
            clearTimeout(t)
            openSfx.pause()
            openSfx.currentTime = 0
        }
    }, [])

    // background music while the bonus is open (stops when it closes)
    useEffect(() => {
        if (!BG_MUSIC) return
        const bg = new Audio('assets/ice-bonus/fundo_bonusI.wav')
        bg.loop = true
        bg.volume = getAudioVolume()
        bgRef.current = bg

        // play() is rejected — silently, the promise is the only signal — while the
        // document has no user activation, which is the normal state when the game
        // runs inside a lobby iframe. The first gesture that reaches us re-arms it,
        // and the listeners come off as soon as the loop is actually running.
        const GESTURES = ['pointerdown', 'keydown', 'touchstart']
        const stopListening = () => GESTURES.forEach((e) => window.removeEventListener(e, start))
        const start = () => { bg.play().then(stopListening).catch(() => { }) }
        start()
        GESTURES.forEach((e) => window.addEventListener(e, start))

        return () => {
            stopListening()
            bg.pause()
            bg.currentTime = 0
            bgRef.current = null
        }
    }, [])

    // warm the exit fanfare while the door is still opening — it is 344KB of
    // uncompressed wav and has to start on the beat the last card settles
    useEffect(() => { warmExitFanfare() }, [])

    // and PARABÉNS!!!, so the message bar does not go blank for a frame on the win
    useEffect(() => { new Image().src = `${BASE}/msg_congrats.png` }, [])

    // the hand-back is what unmounts this bonus, so by here the close has already
    // fired; this is only so nothing is left pending on any other route out
    useEffect(() => () => clearTimeout(exitTimerRef.current), [])

    // opens one card: reveal animation + sound, then its number from getBonusResult.
    // Called by a click and, once the round is won, by the auto-reveal below — which
    // passes `fixedValue`, the number to print without asking the backend for it.
    const openCard = (i, p, fixedValue = null) => {
        setRevealed((prev) => ({ ...prev, [i]: true }))
        // reveal sound — a penguin can define its own `sfx`, otherwise the card-pull one
        const sfx = new Audio(`assets/ice-bonus/${p.sfx || 'tira_papel.wav'}`)
        sfx.volume = getAudioVolume()
        sfx.play().catch(() => { })

        // a decoration card: the round is already decided, so it prints the number it
        // was handed instead of spending a getBonusResult on a tile that no longer pays
        if (fixedValue != null) {
            setFiller((prev) => ({ ...prev, [i]: true }))
            setPrizes((prev) => ({ ...prev, [i]: fixedValue }))
            return
        }

        // no round to ask about — the card opens empty rather than showing a number
        // the backend never paid
        if (!gameSessionRecordId) {
            setPrizes((prev) => ({ ...prev, [i]: 0 }))
            return
        }

        // one getBonusResult per card: the slot's index is its tile number, and the
        // number lands on the card as the reveal animation finishes
        const payload = { bonusType, recordId: gameSessionRecordId, tileNumber: i }
        callPostApi(SET_GAME_BONUS_RESULT_POST, payload, {}, (response) => {
            pickPendingRef.current = false
            if (response?.errorCode !== 0) {
                console.error('getBonusResult failed:', response?.message || 'Unknown error')
                return
            }
            const res = response?.responsePacket
            const d = res?.bonusDetails
            // `boxValue` is the number printed on the card. `bonus` stays 0 until the
            // third card of a set comes up, when the backend flips `matched` and pays.
            const value = Math.round(d?.boxValue || 0)
            setPrizes((prev) => ({ ...prev, [i]: value }))
            // matched → this card closed the set, so ITS value is the winning one
            if (d?.matched) { setWon(Math.round(d?.bonus || 0)); setWonVal(value) }
            totalWinRef.current = res?.totalWinnings || totalWinRef.current
        })
    }

    // the auto-reveal fires from a timer whose closure is several renders old, so it
    // reaches openCard (and the maps it reads) through refs rather than the closure
    const openCardRef = useRef(openCard)
    openCardRef.current = openCard
    const revealedRef = useRef(revealed)
    revealedRef.current = revealed
    const prizesRef = useRef(prizes)
    prizesRef.current = prizes
    const fillerRef = useRef(filler)
    fillerRef.current = filler

    const clickPenguin = (i, p) => {
        if (!p.anim || revealed[i] || pickPendingRef.current) return
        // one pick at a time, so two quick clicks cannot race; the response clears it
        if (gameSessionRecordId) pickPendingRef.current = true
        openCard(i, p)
    }

    const allRevealed = Object.keys(prizes).length === penguins.length

    // GANHO is whatever the backend paid, and nothing else.
    //
    // This used to count matching numbers on the shelves itself and fall back to
    // Math.max once every card was open. Both were wrong, and both showed: three
    // cards reading the same number went red on a response that plainly said
    // "matched": false, and a round that matched nothing still finished with its
    // biggest number blinking as though it had won. Whether three of a kind pays
    // is the backend's call — it has the round's rules and can answer matched =
    // false on a board that looks like a set — so nothing here second-guesses it.
    const ganho = won != null ? won : 0

    // the matched card's number turns red and blinks, along with the others showing
    // that same value, and keeps blinking while the round runs out the clock below.
    // No match, no red — not even on the last card standing.
    const hotVal = wonVal

    // Won — so nothing is at stake any more: pop the cards that are still shut, in a
    // random order, so the player gets to see what the rest of the shelves held. Runs
    // once (`won` does not change again), and each card it opens pushes the
    // close-out timer below back by another WIN_HOLD.
    const roundWon = won != null
    const autoOpenedRef = useRef(false)
    useEffect(() => {
        if (!roundWon || autoOpenedRef.current) return
        autoOpenedRef.current = true
        const left = shuffled(
            penguins.map((p, i) => i).filter((i) => penguins[i].anim && !revealedRef.current[i])
        )
        // the pool is what the backend actually priced this round, so the leftovers
        // read like the rest of the shelves instead of invented round numbers
        const timers = left.map((i, n) => setTimeout(() => {
            const pool = Object.entries(prizesRef.current)
                .filter(([j, v]) => !fillerRef.current[j] && v > 0)
                .map(([, v]) => v)
            const value = pool.length ? pool[Math.floor(Math.random() * pool.length)] : DEFAULT_BOX_VALUE
            openCardRef.current(i, penguins[i], value)
        }, (n + 1) * AUTO_OPEN_STEP))
        return () => timers.forEach(clearTimeout)
    }, [roundWon, penguins])

    // The exit fanfare, the moment the round is decided. It takes over from the
    // background loop and plays across the winning set blinking red and the shelves
    // emptying themselves out, so the piece runs under the end of the round rather
    // than after it — started on the settled scene instead, its 8s would all be spent
    // on a still picture.
    const fanfareStartRef = useRef(0)
    useEffect(() => {
        if (won == null && !allRevealed) return
        if (fanfareStartRef.current) return
        fanfareStartRef.current = Date.now()
        playExitFanfare()
        const bg = bgRef.current
        if (bg) { bg.pause(); bg.currentTime = 0 }
    }, [won, allRevealed])

    // The round is done the moment 3 cards match — that is what the bottom bar asks
    // for — or when there is nothing left to open. Let the winning set blink red for
    // WIN_HOLD, and hand the win back to the game once the fanfare has also played
    // out. The fridge used to close on WIN_HOLD alone, which dropped the player back
    // on the reels with the piece still running behind the slot — the bonus has to
    // stay up for its own music.
    const revealCount = Object.keys(revealed).length
    useEffect(() => {
        if (won == null && !allRevealed) return
        // once the hand-back is scheduled nothing re-enters. Two reasons it must not:
        // `onBonusRoundCompleted` is an inline arrow in the wrapper, a new function on
        // every render the game does, and clearing a scheduled close on a re-run
        // would strand the round in the bonus with nothing to get it out.
        if (closingRef.current) return
        const t = setTimeout(() => {
            closingRef.current = true
            // whatever is left of the piece — 0 if the shelves took longer to empty
            // than the music took to play
            const left = EXIT_FANFARE_MS - (Date.now() - fanfareStartRef.current)
            // a timer, not the element's 'ended': a play() the browser rejects for
            // want of user activation never fires it, and the round would never close
            exitTimerRef.current = setTimeout(() => {
                if (onBonusRoundCompleted) onBonusRoundCompleted(totalWinRef.current || ganho)
            }, Math.max(0, left))
        }, WIN_HOLD)
        return () => clearTimeout(t)
        // revealCount is in here on purpose: every card the auto-reveal opens restarts
        // the hold, so the fridge closes WIN_HOLD after the LAST one, not the first.
    }, [allRevealed, ganho, won, revealCount, onBonusRoundCompleted])

    return (
        <div className='refrigerator-bonus'>
            {/* solid room backdrop — stays opaque so the cross-fade never flashes */}
            <img className='refrigerator-bg-image' src={`${BASE}/background.png`} alt='' />

            {/* door opening intro — plays once, then fades out */}
            <img className={`refrigerator-bg-gif ${done ? 'fade-out' : ''}`} src={gifSrc} alt='' />

            {/* final open-fridge scene + penguins (fade in after the door opens) */}
            <div className={`fridge-stage ${done ? 'show' : ''}`}>
                <img className='refrigerator-final-image' src={`${BASE}/door_animation/door-final-image.png`} alt='' />
                <img className='ac-fridge' src={`${BASE}/ac-fridge.png`} alt='' />
                {penguins.map((p, i) => {
                    if (!p.anim) return (
                        <img
                            key={i}
                            className={`fridge-penguin ${p.cls || ''}`}
                            style={{ left: `${p.left}%`, top: `${p.top}%` }}
                            src={`${BASE}/sprites/${p.img}`}
                            alt={`penguin-${i + 1}`}
                        />
                    )

                    // the sheet's real grid — a plain one-row sheet is cols = frames
                    const cols = p.cols || p.frames
                    const rows = p.rows || 1
                    // where the played row sits, and where its LAST frame sits. A row
                    // can be shorter than the grid (005: 9 frames in 10 columns), so
                    // the sweep has to stop on frame `frames - 1`, not at 100%.
                    const posY = rows > 1 ? `${(p.row || 0) / (rows - 1) * 100}%` : '0%'
                    const endX = cols > 1 ? `${(p.frames - 1) / (cols - 1) * 100}%` : '0%'
                    // one of the cards that won — only its NUMBER goes red and blinks,
                    // the penguin itself stays put. `hotVal == null` until the
                    // backend says matched, so an unmatched round has no red at all.
                    const isWinner = hotVal != null && prizes[i] === hotVal && !filler[i]

                    // sprite-sheet penguin: idle frame 0, plays the reveal on click
                    return (
                        <div
                            key={i}
                            className={`fridge-penguin fridge-penguin-anim ${p.cls || ''}`}
                            style={{
                                left: `${p.left}%`,
                                top: `${p.top}%`,
                                aspectRatio: `${p.aw} / ${p.ah}`,
                                backgroundImage: `url(${BASE}/sprites/${p.anim})`,
                                backgroundSize: `${cols * 100}% ${rows * 100}%`,
                                backgroundPositionY: posY,
                                '--penguin-end-x': endX,
                                ...(revealed[i]
                                    ? { animation: `penguin-reveal .8s steps(${p.frames}, jump-none) forwards` }
                                    : {}),
                            }}
                            onClick={() => clickPenguin(i, p)}
                            role='button'
                            aria-label={`penguin-${i + 1}`}
                        >
                            {prizes[i] != null && (
                                <span
                                    className={`fridge-bonus-num ${isWinner ? 'is-max' : ''}`}
                                    style={{ left: `${p.numLeft || 50}%`, top: `${p.numTop || 66}%` }}
                                >{prizes[i]}</span>
                            )}
                        </div>
                    )
                })}

                {/* shelf front rails (mask2remake) drawn IN FRONT of the penguins so
                    their feet tuck behind → looks like they're standing on the shelf.
                    Adjust left/top/width % to line each rail up with a shelf. */}


                {/* bottom message bar (ENCONTRE 3 PINGUINS… → PARABÉNS!!! once won, as in
                    text-video.mp4) + GANHO box */}
                <div className='fridge-msg' style={{ backgroundImage: `url(${BASE}/msg_area.png)` }}>
                    <img className='fridge-msg-text'
                        src={`${BASE}/${roundWon ? 'msg_congrats.png' : 'msg_choose.png'}`} alt='' />
                    <div className='fridge-ganho'>
                        <img className='fridge-ganho-img' src={`${BASE}/msg_win.png`} alt='' />
                        <span className='fridge-ganho-val'>{ganho || ''}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RefrigeratorBonus
